import {
  Address,
  AmountFromBigInt,
  ChainPublicClientService,
  ProtocolKeyService,
} from "@silo/types";
import { Effect, pipe } from "effect";
import { erc20Abi, getContract } from "viem";
import { siloLensAddress } from "@silo/contract-addresses";
import { siloLensAbi } from "@silo/contract-abi-types";
import { Schema } from "@effect/schema";
import { getCurrentTimestamp } from "@silo/utils";
import { ContractsClientError } from "../contracts-client/types";
import { requireAnySettled, settledOrZero } from "../utils/settle-reverts";

interface GetDashboardAssetUserStateParams {
  user: Address;
  marketAddress: Address;
  tokenAddress: Address;
  protectedShareToken: Address;
}

/**
 * Dashboard-only user state fetcher.
 *
 * Fetches the three balances the dashboard renders per asset:
 * - collateralBalance (via SiloLens.getDepositAmount)
 * - protectedBalance (via the protected share token's ERC20.balanceOf)
 * - debtBalance (via SiloLens.getBorrowAmount at the current timestamp)
 *
 * Skips: wallet tokenBalance and projectedDebtBalance — neither is shown on
 * the dashboard. Both still live in the shared `getMarketAssetUserState` for
 * other features that need them.
 */
export function getDashboardAssetUserState({
  user,
  marketAddress,
  tokenAddress,
  protectedShareToken,
}: GetDashboardAssetUserStateParams) {
  return Effect.gen(function* () {
    const protocolKey = yield* ProtocolKeyService;
    const { publicClient } = yield* ChainPublicClientService;

    const currentTimestamp = getCurrentTimestamp();

    const siloLensContract = getContract({
      address: siloLensAddress[protocolKey],
      abi: siloLensAbi,
      client: publicClient,
    });

    const protectedShareTokenContract = getContract({
      address: protectedShareToken,
      abi: erc20Abi,
      client: publicClient,
    });

    const encoded = yield* Effect.tryPromise({
      try: async () => {
        const results = requireAnySettled(
          await Promise.allSettled([
            siloLensContract.read.getDepositAmount([
              marketAddress,
              tokenAddress,
              user,
              currentTimestamp,
            ]),
            protectedShareTokenContract.read.balanceOf([user]),
            siloLensContract.read.getBorrowAmount([
              marketAddress,
              tokenAddress,
              user,
              currentTimestamp,
            ]),
          ])
        );

        const [collateralBalance, protectedBalance, debtBalance] =
          results.map(settledOrZero);

        return { collateralBalance, protectedBalance, debtBalance };
      },
      catch: (cause) => new ContractsClientError({ cause }),
    });

    return yield* pipe(encoded, Schema.decode(DashboardAssetUserState));
  }).pipe(
    Effect.tapError((error) =>
      Effect.logError(
        `Failed ❌: getDashboardAssetUserState market=${marketAddress} token=${tokenAddress} user=${user}: ${
          (error as { _tag?: string })._tag ?? String(error)
        }`
      )
    )
  );
}

export type DashboardAssetUserState = typeof DashboardAssetUserState.Type;
export type DashboardAssetUserStateEncoded =
  typeof DashboardAssetUserState.Encoded;

export const DashboardAssetUserState = Schema.Struct({
  collateralBalance: AmountFromBigInt,
  protectedBalance: AmountFromBigInt,
  debtBalance: AmountFromBigInt,
}).annotations({ identifier: "DashboardAssetUserState" });
